import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";

interface ReceiptItem {
  name: string;
  amount: number;
  category: "food" | "household" | "health" | "personal" | "other";
}

interface Receipt {
  id: string; // receipts.id is a UUID
  date: string;
  store: string;
  total: number;
  items: ReceiptItem[];
  imageUrl: string;
  currency?: string;
}

interface CategoryBreakdownProps {
  receipts: Receipt[];
  preferredCurrency: string;
  convertAmount: (amount: number, fromCurrency: string) => number;
}

const categoryInfo = {
  food: {
    icon: "🍎",
    color: "bg-category-food",
    lightColor: "bg-category-food/20"
  },
  household: {
    icon: "🏠",
    color: "bg-category-household",
    lightColor: "bg-category-household/20"
  },
  health: {
    icon: "💊",
    color: "bg-category-health",
    lightColor: "bg-category-health/20"
  },
  personal: {
    icon: "🧴",
    color: "bg-category-personal",
    lightColor: "bg-category-personal/20"
  },
  other: {
    icon: "📦",
    color: "bg-category-other",
    lightColor: "bg-category-other/20"
  },
};

type TimePeriod = "week" | "month" | "quarter" | "year" | "all";

export const CategoryBreakdown = ({ receipts, preferredCurrency, convertAmount }: CategoryBreakdownProps) => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>("month");

  const timePeriodOptions = [
    { value: "week" as const, label: t('categoryBreakdown.periods.lastWeek') },
    { value: "month" as const, label: t('categoryBreakdown.periods.lastMonth') },
    { value: "quarter" as const, label: t('categoryBreakdown.periods.lastQuarter') },
    { value: "year" as const, label: t('categoryBreakdown.periods.lastYear') },
    { value: "all" as const, label: t('categoryBreakdown.periods.allTime') },
  ];

  const getFilteredReceipts = () => {
    if (selectedPeriod === "all") return receipts;

    const now = new Date();
    const cutoffDate = new Date();

    switch (selectedPeriod) {
      case "week":
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case "month":
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case "quarter":
        cutoffDate.setMonth(now.getMonth() - 3);
        break;
      case "year":
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
    }

    return receipts.filter(receipt => new Date(receipt.date) >= cutoffDate);
  };

  const filteredReceipts = getFilteredReceipts();
  
  // Convert all amounts to preferred currency before aggregating
  const categoryTotals = filteredReceipts.reduce((acc, receipt) => {
    receipt.items.forEach(item => {
      const convertedAmount = convertAmount(item.amount, receipt.currency || 'USD');
      acc[item.category] = (acc[item.category] || 0) + convertedAmount;
    });
    return acc;
  }, {} as Record<string, number>);

  const totalSpent = Object.values(categoryTotals).reduce((sum, amount) => sum + amount, 0);

  const sortedCategories = Object.entries(categoryTotals)
    .sort(([,a], [,b]) => b - a)
    .map(([category, amount]) => ({
      category: category as keyof typeof categoryInfo,
      amount,
      percentage: totalSpent > 0 ? (amount / totalSpent) * 100 : 0
    }));

  return (
    <Card className="shadow-custom-md border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              📊 {t('categoryBreakdown.title')}
            </CardTitle>
            <CardDescription>
              {t('categoryBreakdown.description')}
            </CardDescription>
          </div>
          <Select value={selectedPeriod} onValueChange={(value: TimePeriod) => setSelectedPeriod(value)}>
            <SelectTrigger className="w-40 bg-card border-border shadow-custom-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border shadow-custom-lg z-50">
              {timePeriodOptions.map((option) => (
                <SelectItem 
                  key={option.value} 
                  value={option.value}
                  className="hover:bg-accent focus:bg-accent"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {sortedCategories.map(({ category, amount, percentage }) => {
          const info = categoryInfo[category];
          return (
            <div key={category} className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{info.icon}</span>
                  <span className="font-medium">{t(`categories.${category}`)}</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{formatCurrency(amount, preferredCurrency)}</div>
                  <div className="text-xs text-muted-foreground">
                    {percentage.toFixed(1)}%
                  </div>
                </div>
              </div>
              <div className="relative">
                <Progress 
                  value={percentage} 
                  className="h-2"
                />
                <div 
                  className={cn(
                    "absolute top-0 left-0 h-2 rounded-full transition-all duration-500",
                    info.color
                  )}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}

        <div className="pt-4 border-t">
          <div className="flex justify-between items-center font-semibold">
            <span>{t('categoryBreakdown.totalSpending')}</span>
            <span className="text-lg text-primary">{formatCurrency(totalSpent, preferredCurrency)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};